@file:OptIn(ExperimentalMaterial3Api::class)

package com.example.monetix.ui.screens

import androidx.compose.animation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.horizontalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.rounded.AccountBalance
import androidx.compose.material.icons.rounded.Share
import androidx.compose.material.icons.rounded.Add
import androidx.compose.material.icons.rounded.Delete
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.russhwolf.settings.Settings
import com.example.monetix.db.AccountEntity
import com.example.monetix.ui.components.MonetixTextField
import com.example.monetix.ui.components.MonetixGlassSwitch

data class DynamicIban(
    var iban: String = "", var country: String = "IT", var cinEu: String = "", 
    var cinIt: String = "", var abi: String = "", var cab: String = "", 
    var cc: String = "", var bic: String = ""
)

data class DynamicCard(
    var pan: String = "", var expiry: String = "", var circuit: String = ""
)

data class DynamicBooklet(
    var number: String = "", var office: String = ""
)

@Composable
fun AccountDetailsDialog(
    account: AccountEntity,
    settings: Settings,
    savedIban: String,
    onDismiss: () -> Unit,
    onSave: (String, Map<String, String>, String) -> Unit,
    onShare: (String) -> Unit,
    triggerHaptic: () -> Unit
) {
    val accId = account.id

    var detailsType by remember { mutableStateOf(settings.getString("iban_${accId}_type", "IBAN")) }
    var holderInput by remember { mutableStateOf(settings.getString("iban_${accId}_holder", "")) }

    // DYNAMIC LISTS
    val ibansList = remember { mutableStateListOf<DynamicIban>() }
    val cardsList = remember { mutableStateListOf<DynamicCard>() }
    val bookletsList = remember { mutableStateListOf<DynamicBooklet>() }

    // MACRO SWITCHES FOR SHARE
    var shareIncHolder by remember { mutableStateOf(true) }
    var shareIncIban by remember { mutableStateOf(true) }
    var shareIncCards by remember { mutableStateOf(true) }
    var shareIncBooklets by remember { mutableStateOf(true) }

    LaunchedEffect(Unit) {
        // Load IBANs
        val legacyIban = settings.getString("user_iban_${accId}", "")
        if (legacyIban.isNotBlank() || settings.getString("iban_${accId}_country", "").isNotBlank()) {
            ibansList.add(
                DynamicIban(
                    legacyIban,
                    settings.getString("iban_${accId}_country", "IT"),
                    settings.getString("iban_${accId}_cin_eu", ""),
                    settings.getString("iban_${accId}_cin_it", ""),
                    settings.getString("iban_${accId}_abi", ""),
                    settings.getString("iban_${accId}_cab", ""),
                    settings.getString("iban_${accId}_cc", ""),
                    settings.getString("iban_${accId}_bic", "")
                )
            )
        }
        val iCount = settings.getInt("iban_${accId}_dyn_count", 0)
        for (i in 0 until iCount) {
            ibansList.add(DynamicIban(
                settings.getString("iban_${accId}_dyn_${i}_iban", ""),
                settings.getString("iban_${accId}_dyn_${i}_country", "IT"),
                settings.getString("iban_${accId}_dyn_${i}_cineu", ""),
                settings.getString("iban_${accId}_dyn_${i}_cinit", ""),
                settings.getString("iban_${accId}_dyn_${i}_abi", ""),
                settings.getString("iban_${accId}_dyn_${i}_cab", ""),
                settings.getString("iban_${accId}_dyn_${i}_cc", ""),
                settings.getString("iban_${accId}_dyn_${i}_bic", "")
            ))
        }
        if (ibansList.isEmpty()) ibansList.add(DynamicIban(iban = savedIban))

        // Load CARDS
        val c1Pan = settings.getString("iban_${accId}_card_num", "")
        if (c1Pan.isNotBlank()) cardsList.add(DynamicCard(c1Pan, settings.getString("iban_${accId}_card_exp", ""), settings.getString("iban_${accId}_card_circuit", "")))
        val c2Pan = settings.getString("iban_${accId}_card2_num", "")
        if (c2Pan.isNotBlank()) cardsList.add(DynamicCard(c2Pan, settings.getString("iban_${accId}_card2_exp", ""), settings.getString("iban_${accId}_card2_circuit", "")))

        val cCount = settings.getInt("card_${accId}_dyn_count", 0)
        for (i in 0 until cCount) {
            cardsList.add(DynamicCard(
                settings.getString("card_${accId}_dyn_${i}_pan", ""),
                settings.getString("card_${accId}_dyn_${i}_exp", ""),
                settings.getString("card_${accId}_dyn_${i}_circ", "")
            ))
        }
        if (cardsList.isEmpty()) cardsList.add(DynamicCard())

        // Load BOOKLETS
        val b1Num = settings.getString("iban_${accId}_booklet_num", "")
        if (b1Num.isNotBlank()) bookletsList.add(DynamicBooklet(b1Num, settings.getString("iban_${accId}_booklet_office", "")))

        val bCount = settings.getInt("booklet_${accId}_dyn_count", 0)
        for (i in 0 until bCount) {
            bookletsList.add(DynamicBooklet(
                settings.getString("booklet_${accId}_dyn_${i}_num", ""),
                settings.getString("booklet_${accId}_dyn_${i}_off", "")
            ))
        }
        if (bookletsList.isEmpty()) bookletsList.add(DynamicBooklet())
    }

    fun buildShareMessage(): String {
        return buildString {
            appendLine("🏛️ COORDINATE · ${account.name.uppercase()}")
            appendLine()
            if (shareIncHolder && holderInput.isNotBlank()) appendLine("👤 Intestatario: ${holderInput.trim()}")
            appendLine("🏦 Banca / Conto: ${account.name}")

            if (shareIncIban) {
                ibansList.forEachIndexed { i, item ->
                    val c = item.country.trim().uppercase()
                    val ce = item.cinEu.trim()
                    val ci = item.cinIt.trim().uppercase()
                    val a = item.abi.trim()
                    val cb = item.cab.trim()
                    val cc = item.cc.trim()
                    val composed = if (c.isNotBlank() && a.isNotBlank() && cb.isNotBlank() && cc.isNotBlank()) "$c$ce $ci $a $cb $cc" else ""
                    val finalIb = item.iban.ifBlank { composed }
                    if (finalIb.isNotBlank()) {
                        appendLine("💳 IBAN ${i+1}: $finalIb")
                        if (item.bic.isNotBlank()) appendLine("🌐 BIC/SWIFT: ${item.bic.trim()}")
                    }
                }
            }

            if (shareIncCards) {
                cardsList.filter { it.pan.isNotBlank() }.forEachIndexed { i, c ->
                    appendLine("💳 Carta ${i+1}: ${c.pan.trim()}${if(c.expiry.isNotBlank()) " (Scad: ${c.expiry.trim()})" else ""}${if(c.circuit.isNotBlank()) " - ${c.circuit.trim()}" else ""}")
                }
            }

            if (shareIncBooklets) {
                bookletsList.filter { it.number.isNotBlank() }.forEachIndexed { i, b ->
                    appendLine("📖 Libretto ${i+1}: ${b.number.trim()}${if(b.office.isNotBlank()) " (Uff: ${b.office.trim()})" else ""}")
                }
            }

            appendLine()
            append("Inviato tramite Monetix 🚀")
        }.trimIndent()
    }

    AlertDialog(
        onDismissRequest = onDismiss,
        shape = RoundedCornerShape(28.dp),
        containerColor = Color(0xFF1E1B4B),
        title = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Rounded.AccountBalance, null, tint = Color(0xFFF59E0B), modifier = Modifier.size(24.dp))
                Spacer(Modifier.width(8.dp))
                Text("Coordinate · ${account.name}", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold), color = Color.White)
            }
        },
        text = {
            Column(modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState()), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                // TABS
                Row(modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    FilterChip(selected = detailsType == "IBAN", onClick = { detailsType = "IBAN" }, label = { Text("Conti / IBAN", fontWeight = FontWeight.Bold) }, colors = FilterChipDefaults.filterChipColors(selectedContainerColor = Color.White, selectedLabelColor = Color.Black, containerColor = Color.White.copy(alpha = 0.10f), labelColor = Color.White), border = null)
                    FilterChip(selected = detailsType == "CARD", onClick = { detailsType = "CARD" }, label = { Text("Carte Ricaricabili", fontWeight = FontWeight.Bold) }, colors = FilterChipDefaults.filterChipColors(selectedContainerColor = Color.White, selectedLabelColor = Color.Black, containerColor = Color.White.copy(alpha = 0.10f), labelColor = Color.White), border = null)
                    FilterChip(selected = detailsType == "BOOKLET", onClick = { detailsType = "BOOKLET" }, label = { Text("Libretti Postali", fontWeight = FontWeight.Bold) }, colors = FilterChipDefaults.filterChipColors(selectedContainerColor = Color.White, selectedLabelColor = Color.Black, containerColor = Color.White.copy(alpha = 0.10f), labelColor = Color.White), border = null)
                }

                MonetixTextField(
                    value = holderInput, onValueChange = { holderInput = it },
                    label = { Text("Intestatario (Nome e Cognome)", color = Color(0xFFA0A0A0)) },
                    singleLine = true, modifier = Modifier.fillMaxWidth(),
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                AnimatedVisibility(visible = detailsType == "IBAN") {
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        ibansList.forEachIndexed { index, item ->
                            HorizontalDivider(color = Color.White.copy(alpha = 0.1f), modifier = Modifier.padding(vertical = 2.dp))
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                Text("COORDINATE IBAN ${index+1}", style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Bold), color = Color(0xFF60A5FA))
                                if (ibansList.size > 1) {
                                    IconButton(onClick = { ibansList.removeAt(index) }, modifier = Modifier.size(24.dp)) { Icon(Icons.Rounded.Delete, null, tint = MaterialTheme.colorScheme.error) }
                                }
                            }

                            MonetixTextField(
                                value = item.iban, onValueChange = { clean ->
                                    val up = clean.replace(" ", "").uppercase()
                                    val updated = item.copy(iban = up)
                                    if (up.length == 27 && up.startsWith("IT")) {
                                        updated.country = "IT"
                                        updated.cinEu = up.substring(2, 4)
                                        updated.cinIt = up.substring(4, 5)
                                        updated.abi = up.substring(5, 10)
                                        updated.cab = up.substring(10, 15)
                                        updated.cc = up.substring(15, 27)
                                    }
                                    ibansList[index] = updated
                                },
                                label = { Text("IBAN Completo", color = Color(0xFF60A5FA)) },
                                singleLine = true, modifier = Modifier.fillMaxWidth(),
                                colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                            )
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                                MonetixTextField(value = item.country, onValueChange = { ibansList[index] = item.copy(country = it.uppercase().take(2)) }, label = { Text("Paese", color = Color(0xFFA0A0A0)) }, singleLine = true, modifier = Modifier.weight(1f), colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f)))
                                MonetixTextField(value = item.cinEu, onValueChange = { if (it.length <= 2 && it.all { c -> c.isDigit() }) ibansList[index] = item.copy(cinEu = it) }, label = { Text("CIN/EU", color = Color(0xFFA0A0A0)) }, singleLine = true, modifier = Modifier.weight(1f), colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f)))
                                MonetixTextField(value = item.cinIt, onValueChange = { ibansList[index] = item.copy(cinIt = it.uppercase().take(1)) }, label = { Text("CIN/IT", color = Color(0xFFA0A0A0)) }, singleLine = true, modifier = Modifier.weight(1f), colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f)))
                            }
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                                MonetixTextField(value = item.abi, onValueChange = { if (it.length <= 5 && it.all { c -> c.isDigit() }) ibansList[index] = item.copy(abi = it) }, label = { Text("ABI", color = Color(0xFFA0A0A0)) }, singleLine = true, modifier = Modifier.weight(1f), colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f)))
                                MonetixTextField(value = item.cab, onValueChange = { if (it.length <= 5 && it.all { c -> c.isDigit() }) ibansList[index] = item.copy(cab = it) }, label = { Text("CAB", color = Color(0xFFA0A0A0)) }, singleLine = true, modifier = Modifier.weight(1f), colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f)))
                            }
                            MonetixTextField(value = item.cc, onValueChange = { ibansList[index] = item.copy(cc = it.uppercase().take(12)) }, label = { Text("Conto Corrente", color = Color(0xFFA0A0A0)) }, singleLine = true, modifier = Modifier.fillMaxWidth(), colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f)))
                            MonetixTextField(value = item.bic, onValueChange = { ibansList[index] = item.copy(bic = it.uppercase().take(11)) }, label = { Text("BIC/SWIFT", color = Color(0xFFA0A0A0)) }, singleLine = true, modifier = Modifier.fillMaxWidth(), colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f)))
                        }

                        TextButton(onClick = { ibansList.add(DynamicIban()) }, modifier = Modifier.fillMaxWidth()) {
                            Icon(Icons.Rounded.Add, null, tint = Color(0xFF60A5FA))
                            Spacer(Modifier.width(8.dp))
                            Text("Aggiungi altro IBAN", color = Color(0xFF60A5FA), fontWeight = FontWeight.Bold)
                        }
                    }
                }

                AnimatedVisibility(visible = detailsType == "CARD") {
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        cardsList.forEachIndexed { index, item ->
                            HorizontalDivider(color = Color.White.copy(alpha = 0.1f), modifier = Modifier.padding(vertical = 2.dp))
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                Text("CARTA ${index+1} (PAN E SCADENZA)", style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Bold), color = Color(0xFF10B981))
                                if (cardsList.size > 1) {
                                    IconButton(onClick = { cardsList.removeAt(index) }, modifier = Modifier.size(24.dp)) { Icon(Icons.Rounded.Delete, null, tint = MaterialTheme.colorScheme.error) }
                                }
                            }

                            MonetixTextField(value = item.pan, onValueChange = { cardsList[index] = item.copy(pan = it) }, label = { Text("Numero Carta", color = Color(0xFF10B981)) }, singleLine = true, modifier = Modifier.fillMaxWidth(), colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF10B981), unfocusedBorderColor = Color.White.copy(alpha = 0.20f)))
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                                MonetixTextField(value = item.expiry, onValueChange = { cardsList[index] = item.copy(expiry = it) }, label = { Text("Scadenza", color = Color(0xFFA0A0A0)) }, singleLine = true, modifier = Modifier.weight(1f), colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF10B981), unfocusedBorderColor = Color.White.copy(alpha = 0.20f)))
                                MonetixTextField(value = item.circuit, onValueChange = { cardsList[index] = item.copy(circuit = it) }, label = { Text("Circuito", color = Color(0xFFA0A0A0)) }, singleLine = true, modifier = Modifier.weight(1f), colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF10B981), unfocusedBorderColor = Color.White.copy(alpha = 0.20f)))
                            }
                        }
                        TextButton(onClick = { cardsList.add(DynamicCard()) }, modifier = Modifier.fillMaxWidth()) {
                            Icon(Icons.Rounded.Add, null, tint = Color(0xFF10B981))
                            Spacer(Modifier.width(8.dp))
                            Text("Aggiungi altra Carta", color = Color(0xFF10B981), fontWeight = FontWeight.Bold)
                        }
                    }
                }

                AnimatedVisibility(visible = detailsType == "BOOKLET") {
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        bookletsList.forEachIndexed { index, item ->
                            HorizontalDivider(color = Color.White.copy(alpha = 0.1f), modifier = Modifier.padding(vertical = 2.dp))
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                Text("LIBRETTO ${index+1} / RISPARMIO", style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Bold), color = Color(0xFFF59E0B))
                                if (bookletsList.size > 1) {
                                    IconButton(onClick = { bookletsList.removeAt(index) }, modifier = Modifier.size(24.dp)) { Icon(Icons.Rounded.Delete, null, tint = MaterialTheme.colorScheme.error) }
                                }
                            }

                            MonetixTextField(value = item.number, onValueChange = { bookletsList[index] = item.copy(number = it) }, label = { Text("Numero Libretto", color = Color(0xFFF59E0B)) }, singleLine = true, modifier = Modifier.fillMaxWidth(), colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFFF59E0B), unfocusedBorderColor = Color.White.copy(alpha = 0.20f)))
                            MonetixTextField(value = item.office, onValueChange = { bookletsList[index] = item.copy(office = it) }, label = { Text("Ufficio / Frazionario", color = Color(0xFFA0A0A0)) }, singleLine = true, modifier = Modifier.fillMaxWidth(), colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFFF59E0B), unfocusedBorderColor = Color.White.copy(alpha = 0.20f)))
                        }
                        TextButton(onClick = { bookletsList.add(DynamicBooklet()) }, modifier = Modifier.fillMaxWidth()) {
                            Icon(Icons.Rounded.Add, null, tint = Color(0xFFF59E0B))
                            Spacer(Modifier.width(8.dp))
                            Text("Aggiungi altro Libretto", color = Color(0xFFF59E0B), fontWeight = FontWeight.Bold)
                        }
                    }
                }

                // 🎛️ SELEZIONA COSA CONDIVIDERE
                HorizontalDivider(color = Color.White.copy(alpha = 0.1f), modifier = Modifier.padding(vertical = 4.dp))
                Text("COSA VUOI INCLUDERE NELLA CONDIVISIONE?", style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Bold), color = Color(0xFF60A5FA))

                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    Text("Includi IBAN", style = MaterialTheme.typography.bodyMedium, color = Color.White)
                    MonetixGlassSwitch(checked = shareIncIban, onCheckedChange = { shareIncIban = it })
                }
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    Text("Includi Carte", style = MaterialTheme.typography.bodyMedium, color = Color.White)
                    MonetixGlassSwitch(checked = shareIncCards, onCheckedChange = { shareIncCards = it })
                }
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    Text("Includi Libretti", style = MaterialTheme.typography.bodyMedium, color = Color.White)
                    MonetixGlassSwitch(checked = shareIncBooklets, onCheckedChange = { shareIncBooklets = it })
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    val map = mutableMapOf<String, String>()
                    map["type"] = detailsType
                    map["holder"] = holderInput.trim()

                    // Salvataggio Liste per Compatibilità Locale
                    settings.putString("iban_${accId}_type", detailsType)
                    settings.putString("iban_${accId}_holder", holderInput.trim())
                    
                    settings.putInt("iban_${accId}_dyn_count", ibansList.size)
                    ibansList.forEachIndexed { i, ib ->
                        settings.putString("iban_${accId}_dyn_${i}_iban", ib.iban.trim())
                        settings.putString("iban_${accId}_dyn_${i}_country", ib.country.trim())
                        settings.putString("iban_${accId}_dyn_${i}_cineu", ib.cinEu.trim())
                        settings.putString("iban_${accId}_dyn_${i}_cinit", ib.cinIt.trim())
                        settings.putString("iban_${accId}_dyn_${i}_abi", ib.abi.trim())
                        settings.putString("iban_${accId}_dyn_${i}_cab", ib.cab.trim())
                        settings.putString("iban_${accId}_dyn_${i}_cc", ib.cc.trim())
                        settings.putString("iban_${accId}_dyn_${i}_bic", ib.bic.trim())

                        map["dyn_iban_${i}_iban"] = ib.iban.trim()
                        map["dyn_iban_${i}_country"] = ib.country.trim()
                        map["dyn_iban_${i}_cineu"] = ib.cinEu.trim()
                        map["dyn_iban_${i}_cinit"] = ib.cinIt.trim()
                        map["dyn_iban_${i}_abi"] = ib.abi.trim()
                        map["dyn_iban_${i}_cab"] = ib.cab.trim()
                        map["dyn_iban_${i}_cc"] = ib.cc.trim()
                        map["dyn_iban_${i}_bic"] = ib.bic.trim()
                    }

                    settings.putInt("card_${accId}_dyn_count", cardsList.size)
                    cardsList.forEachIndexed { i, c ->
                        settings.putString("card_${accId}_dyn_${i}_pan", c.pan.trim())
                        settings.putString("card_${accId}_dyn_${i}_exp", c.expiry.trim())
                        settings.putString("card_${accId}_dyn_${i}_circ", c.circuit.trim())
                        
                        map["dyn_card_${i}_pan"] = c.pan.trim()
                        map["dyn_card_${i}_exp"] = c.expiry.trim()
                        map["dyn_card_${i}_circ"] = c.circuit.trim()
                    }

                    settings.putInt("booklet_${accId}_dyn_count", bookletsList.size)
                    bookletsList.forEachIndexed { i, b ->
                        settings.putString("booklet_${accId}_dyn_${i}_num", b.number.trim())
                        settings.putString("booklet_${accId}_dyn_${i}_off", b.office.trim())
                        
                        map["dyn_booklet_${i}_num"] = b.number.trim()
                        map["dyn_booklet_${i}_off"] = b.office.trim()
                    }

                    map["dyn_iban_count"] = ibansList.size.toString()
                    map["dyn_card_count"] = cardsList.size.toString()
                    map["dyn_booklet_count"] = bookletsList.size.toString()

                    val primaryIban = ibansList.firstOrNull()?.iban?.trim() ?: cardsList.firstOrNull()?.pan?.trim() ?: ""

                    onSave(accId, map, primaryIban)
                },
                modifier = Modifier.fillMaxWidth().height(48.dp),
                shape = RoundedCornerShape(14.dp),
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF60A5FA), contentColor = Color.White)
            ) {
                Text("SALVA E SINCRONIZZA ☁️", fontWeight = FontWeight.Bold)
            }
        },
        dismissButton = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                TextButton(onClick = onDismiss) { Text("ANNULLA", color = Color.White.copy(alpha = 0.6f), fontWeight = FontWeight.Bold) }
                Spacer(Modifier.weight(1f))
                Button(
                    onClick = {
                        triggerHaptic()
                        onShare(buildShareMessage())
                    },
                    shape = RoundedCornerShape(14.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha = 0.15f), contentColor = Color.White)
                ) {
                    Icon(Icons.Rounded.Share, contentDescription = null, modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(6.dp))
                    Text("CONDIVIDI 📤", fontWeight = FontWeight.Bold)
                }
            }
        }
    )
}
